import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Upload, Download } from 'lucide-react';
import { parseCsvFamilies, downloadFamilyTemplate } from './csvUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BulkFamilyUpload = ({ axiosConfig, onUploaded }) => {
  const [csvText, setCsvText] = useState('');
  const [file, setFile] = useState(null);
  const [parsedFamilies, setParsedFamilies] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setCsvText(text);
      const families = parseCsvFamilies(text);
      setParsedFamilies(families);
      const totalMembers = families.reduce((sum, fam) => sum + fam.members.length, 0);
      toast.success(`File loaded: ${families.length} families with ${totalMembers} members found`);
    };
    reader.readAsText(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const families = parseCsvFamilies(csvText);
      if (families.length === 0) {
        toast.error('No valid families found in CSV');
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      let created = 0;
      let skipped = 0;
      let rejectedFull = 0;
      const explainError = (error) => {
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) return detail.map((d) => `${(d.loc || []).join('.')}: ${d.msg}`).join('; ');
        if (typeof detail === 'string') return detail;
        if (!error.response) return 'Network error — check your connection and try again';
        return `Server error (${error.response.status})`;
      };
      for (const family of families) {
        try {
          const res = await axios.post(`${API}/admin/families/bulk`, family, axiosConfig);
          successCount++;
          created += (res.data.members_created || []).length;
          skipped += (res.data.members_skipped || []).length;
          rejectedFull += (res.data.members_rejected_family_full || []).length;
        } catch (error) {
          errorCount++;
          toast.error(`Failed to create family ${family.family_id}: ${explainError(error)}`);
        }
      }

      if (successCount > 0) {
        const parts = [`✅ Processed ${successCount} ${successCount === 1 ? 'family' : 'families'}: ${created} member(s) created`];
        if (skipped > 0) parts.push(`${skipped} skipped (already exist)`);
        if (rejectedFull > 0) parts.push(`${rejectedFull} rejected (family full)`);
        toast.success(parts.join(', '));
      }
      if (errorCount > 0 && successCount === 0) {
        toast.error(`❌ Failed to create all ${errorCount} families. Please check the data and try again.`);
      }

      setCsvText('');
      setParsedFamilies([]);
      setFile(null);
      onUploaded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload families');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-blue-200 shadow-md">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Upload className="w-5 h-5" />
          Bulk Upload Families via CSV/TSV
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="border rounded-lg p-4 bg-green-50">
            <h3 className="font-semibold mb-2 text-sm">Step 1: Download Template</h3>
            <p className="text-xs text-gray-600 mb-3">
              Download the CSV template and open it in Microsoft Excel to fill in your family data.
            </p>
            <Button type="button" onClick={downloadFamilyTemplate} variant="outline" className="w-full" data-testid="download-family-template">
              <Download className="w-4 h-4 mr-2" />
              Download Family Template
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Format: FIRST NAME, MIDDLE NAME, LAST NAME, D.O.B, SEX, RELATIONSHIP, SERIAL NUMBER, FAMILY ID, ALLOTMENT
            </p>
          </div>

          <div className="space-y-2">
            <Label>Step 2: Upload CSV File</Label>
            <Input type="file" accept=".csv" onChange={handleFileChange} className="cursor-pointer" data-testid="family-csv-input" />
            {file && <p className="text-xs text-green-600">✓ File loaded: {file.name}</p>}
          </div>

          {parsedFamilies.length > 0 && (
            <div className="border rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold mb-3 text-sm">Step 3: Review Families ({parsedFamilies.length} families found)</h3>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-semibold">Family ID</th>
                      <th className="text-left p-2 text-xs font-semibold">Principal Member</th>
                      <th className="text-center p-2 text-xs font-semibold">Members</th>
                      <th className="text-right p-2 text-xs font-semibold">Allotment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedFamilies.map((family) => (
                      <tr key={family.family_id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-mono text-xs">{family.family_id}</td>
                        <td className="p-2 text-xs">{family.principle_member_name}</td>
                        <td className="p-2 text-center text-xs">{family.members.length}</td>
                        <td className="p-2 text-right text-xs font-semibold">${(family.total_allotment || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                ℹ️ Total: {parsedFamilies.length} families with {parsedFamilies.reduce((sum, f) => sum + f.members.length, 0)} members
              </p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || parsedFamilies.length === 0}
            className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg font-semibold"
            data-testid="bulk-upload-families-button"
          >
            {loading ? 'Uploading...' : `Confirm & Upload ${parsedFamilies.length} Families`}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
